import { getApiV1Url } from '@/config/api'

/** 회원 수 API 응답 data */
export interface MembersCountTodayData {
  todayCount: number
  yesterdayCount: number
  totalCount: number
}

/**
 * 적립 수 API를 화면/통계에서 쓰기 위한 정규화 형태
 * (reward-stats/today-summary 응답의 todayEarnCount·yesterdayEarnCount를 매핑)
 */
export interface StampsCountEarnTodayYesterdayData {
  todayCount: number
  yesterdayCount: number
}

/** reward-stats/today-summary API 응답 data */
interface RewardStatsTodaySummaryData {
  todayEarnCount: number
  yesterdayEarnCount: number
}

/** coupon-stats/today-summary dateField */
export type CouponStatsDateField = 'ISSUED_AT' | 'USED_AT' | 'EXPIRE_AT'

/** coupon-stats/today-summary 요청 파라미터 */
export interface CouponStatsTodaySummaryParams {
  status?: string
  dateField: CouponStatsDateField
  startAt: string
  endAt: string
}

/** coupon-stats/today-summary API 응답 data */
export interface CouponStatsTodaySummaryData {
  todayCount: number
  yesterdayCount: number
}

/** 대시보드 통계 preset */
export const COUPON_STATS_PRESET = {
  USED: { status: 'USED', dateField: 'USED_AT' },
  ISSUED: { dateField: 'ISSUED_AT' },
  EXPIRED: { dateField: 'EXPIRE_AT' }
} as const satisfies Record<string, Pick<CouponStatsTodaySummaryParams, 'status' | 'dateField'>>

/** 서버 공통 응답 (data만 사용) */
interface ApiResponse<T> {
  data?: T
  errors?: {
    error?: Array<{ code?: number; target?: string; message?: string }>
  }
}

/**
 * 오늘 날짜를 YYYY-MM-DD 형식으로 반환 (로컬 날짜)
 */
export function getTodayString(): string {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/**
 * 어제 대비 변화율(%) 계산. 어제가 0이면 null.
 */
export function calcChangePercent(
  todayCount: number,
  yesterdayCount: number
): number | null {
  if (yesterdayCount === 0) return null
  return Math.round(((todayCount - yesterdayCount) / yesterdayCount) * 100)
}

/**
 * GET /api/v1/projects/{projectCode}/shops/{shopCode}/shop-customer-stats/today-summary
 * @param startAt ISO-8601 (예: 2026-04-17T00:00:00+09:00)
 * @param endAt ISO-8601 (예: 2026-04-18T00:00:00+09:00)
 */
export async function getMembersCountToday(
  projectCode: string,
  shopCode: string,
  startAt: string,
  endAt: string
): Promise<MembersCountTodayData> {
  const query = new URLSearchParams({
    startAt,
    endAt
  }).toString()
  const url = getApiV1Url(
    `/projects/${projectCode}/shops/${shopCode}/shop-customer-stats/today-summary?${query}`
  )
  const response = await fetch(url)
  const json: ApiResponse<MembersCountTodayData> = await response.json()

  if (!response.ok) {
    const message =
      json?.errors?.error?.[0]?.message ?? `요청 실패 (${response.status})`
    throw new Error(message)
  }

  if (json.data == null) {
    throw new Error('회원 수 데이터를 불러올 수 없습니다.')
  }

  return json.data
}

/**
 * GET /api/v1/projects/{projectCode}/shops/{shopCode}/reward-stats/today-summary
 * @param startAt ISO-8601 (예: 2026-04-17T00:00:00+09:00)
 * @param endAt ISO-8601 (예: 2026-04-18T00:00:00+09:00)
 */
export async function getStampsCountEarnTodayYesterday(
  projectCode: string,
  shopCode: string,
  startAt: string,
  endAt: string
): Promise<StampsCountEarnTodayYesterdayData> {
  const query = new URLSearchParams({
    startAt,
    endAt
  }).toString()
  const url = getApiV1Url(
    `/projects/${projectCode}/shops/${shopCode}/reward-stats/today-summary?${query}`
  )
  const response = await fetch(url)
  const json: ApiResponse<RewardStatsTodaySummaryData> = await response.json()

  if (!response.ok) {
    const message =
      json?.errors?.error?.[0]?.message ?? `요청 실패 (${response.status})`
    throw new Error(message)
  }

  if (json.data == null) {
    throw new Error('적립 수 데이터를 불러올 수 없습니다.')
  }

  const { todayEarnCount, yesterdayEarnCount } = json.data
  if (
    typeof todayEarnCount !== 'number' ||
    typeof yesterdayEarnCount !== 'number'
  ) {
    throw new Error('적립 수 데이터를 불러올 수 없습니다.')
  }

  return {
    todayCount: todayEarnCount,
    yesterdayCount: yesterdayEarnCount
  }
}

/**
 * GET /api/v1/projects/{projectCode}/shops/{shopCode}/coupon-stats/today-summary
 */
export async function getCouponStatsTodaySummary(
  projectCode: string,
  shopCode: string,
  params: CouponStatsTodaySummaryParams
): Promise<CouponStatsTodaySummaryData> {
  const query = new URLSearchParams({
    dateField: params.dateField,
    startAt: params.startAt,
    endAt: params.endAt
  })
  if (params.status != null) {
    query.set('status', params.status)
  }

  const url = getApiV1Url(
    `/projects/${projectCode}/shops/${shopCode}/coupon-stats/today-summary?${query.toString()}`
  )
  const response = await fetch(url)
  const json: ApiResponse<CouponStatsTodaySummaryData> = await response.json()

  if (!response.ok) {
    const message =
      json?.errors?.error?.[0]?.message ?? `요청 실패 (${response.status})`
    throw new Error(message)
  }

  if (json.data == null) {
    throw new Error('쿠폰 통계 데이터를 불러올 수 없습니다.')
  }

  const { todayCount, yesterdayCount } = json.data
  if (typeof todayCount !== 'number' || typeof yesterdayCount !== 'number') {
    throw new Error('쿠폰 통계 데이터를 불러올 수 없습니다.')
  }

  return json.data
}

/** 오늘 쿠폰 사용 건수 (status=USED, dateField=USED_AT) */
export async function getCouponUsedCountTodayYesterday(
  projectCode: string,
  shopCode: string,
  startAt: string,
  endAt: string
): Promise<CouponStatsTodaySummaryData> {
  return getCouponStatsTodaySummary(projectCode, shopCode, {
    ...COUPON_STATS_PRESET.USED,
    startAt,
    endAt
  })
}
